import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import api from "../../lib/api";
import { useLang } from "../../context/LanguageContext";

/**
 * De tre senaste artiklarna ur Spotlight.
 *
 * **Riktiga artiklar, inte tre påhittade rubriker.** Designen levererade
 * "Rapport från 1 847 bröllop. Latin upp 47 %", vilket är en undersökning som
 * inte finns. Sektionen läser i stället ``/api/spotlight`` och visar det som
 * faktiskt är publicerat. Finns inga artiklar renderas ingenting, och sidan
 * hänger ihop ändå.
 *
 * **Ytan heter Spotlight.** Nyheter, trender, tips och guider bor där, och namnet
 * är detsamma i koden, i admin, i routen och för läsaren. Regeln och de
 * synonymer som inte får användas står i ``.claude/skills/talentsio-copy-sv``.
 */

const T = {
  sv: { eyebrow: "Spotlight", heading: "Läsning som gör ert event bättre.", all: "Alla artiklar" },
  en: { eyebrow: "Spotlight", heading: "Reading that makes your event better.", all: "All articles" },
};

const SpotlightPreview = () => {
  const { lang } = useLang();
  const t = T[lang === "sv" ? "sv" : "en"];
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    api.get("/spotlight", { params: { limit: 3 } })
      .then(({ data }) => setPosts(Array.isArray(data) ? data.slice(0, 3) : (data?.posts || []).slice(0, 3)))
      .catch(() => setPosts([]));
  }, []);

  if (!posts || posts.length === 0) return null;
  const base = lang === "sv" ? "/sv/spotlight" : "/spotlight";

  return (
    <section className="border-t border-[#1e1e1e]" data-testid="spotlight-preview">
      <div className="max-w-[1100px] mx-auto px-6 py-20 md:py-24">
        <div className="flex items-end justify-between gap-6 mb-10">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-neutral-500 mb-3">{t.eyebrow}</div>
            <h2 className="text-[30px] md:text-[38px] font-extrabold tracking-tight text-white">{t.heading}</h2>
          </div>
          <Link to={base} data-testid="spotlight-all"
            className="hidden md:inline-flex items-center gap-1.5 text-[14px] text-[#FF7A2F] hover:text-[#FF5C00] transition-colors shrink-0">
            {t.all} <ArrowRight size={15} />
          </Link>
        </div>

        <div className="grid md:grid-cols-3 gap-5">
          {posts.map((p) => (
            <Link key={p.id || p.slug} to={`${base}/${p.slug}`} data-testid={`spotlight-${p.slug}`}
              className="group rounded-xl border border-[#2A2A2A] bg-[#181818] overflow-hidden hover:border-[#3A3A3A] transition-colors">
              <div className="aspect-[4/3] bg-gradient-to-br from-neutral-800 to-black overflow-hidden">
                {p.hero_image_url && (
                  <img src={p.hero_image_url} alt="" loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.03]" />
                )}
              </div>
              <div className="p-5">
                {(p.category || p.read_minutes) && (
                  <div className="text-[11px] uppercase tracking-wider text-neutral-500 mb-2">
                    {[p.category, p.read_minutes && `${p.read_minutes} min`].filter(Boolean).join(" · ")}
                  </div>
                )}
                <div className="text-[18px] font-bold text-white leading-snug mb-2 group-hover:text-[#FF7A2F] transition-colors">
                  {p.title}
                </div>
                {p.excerpt && <p className="text-[13.5px] text-neutral-400 leading-relaxed line-clamp-2">{p.excerpt}</p>}
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SpotlightPreview;
